import { CdkScrollable } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewChild
} from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipListbox, MatChipOption } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { first } from 'rxjs/operators';
import { PendingProviderSelectionService } from '../../../../core/services';
import { AuthPopupService, AuthService } from '../../../auth/services';
import { Provider } from '../../models/provider.model';
import { ProviderCardComponent } from '../provider-card/provider-card.component';

@Component({
    selector: 'app-provider-card-collection',
    templateUrl: './provider-card-collection.component.html',
    styleUrls: ['./provider-card-collection.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        TranslocoModule,
        FlexLayoutModule,
        MatButtonModule,
        MatChipListbox,
        MatChipOption,
        MatIcon,
        MatCardModule,
        MatMenuModule,
        MatProgressSpinnerModule,
        ProviderCardComponent
    ]
})
export class ProviderCardCollectionComponent implements AfterViewInit {
    @ViewChild('headerStart') headerStart!: ElementRef;
    @ViewChild('footerStart') footerStart!: ElementRef;
    @ViewChild('fixedStart') fixedStart!: ElementRef;

    @Input() set providers(values: Provider[] | undefined) {
        if (values) {
            this.selectedProviders.forEach((selected) => {
                values.forEach((provider) => {
                    if (provider.providerId == selected.providerId)
                        provider.selected = true;
                });
            });
            this._providers = values;
            this.updateSelectedProviders(undefined);
        }
    }

    @Input() filtered: boolean = false;
    @Input() loading = false;

    private _providers: Provider[] = [];
    get providers(): Provider[] | undefined {
        return this._providers;
    }
    selectedProviders: Provider[] = [];
    private selectedProvider: Provider | undefined;

    @Output() sortEvent: EventEmitter<string> = new EventEmitter<string>();

    sortOrderText: string = '';

    constructor(
        private router: Router,
        private readonly _auth: AuthService,
        private readonly _authPopup: AuthPopupService,
        private readonly _transloco: TranslocoService,
        private scrollable: CdkScrollable,
        private readonly _pendingSelection: PendingProviderSelectionService
    ) {
        this._transloco
            .selectTranslate('provider.sort-by-options.name-a-to-z')
            .pipe(first())
            .subscribe((text) => (this.sortOrderText = text));
    }

    ngAfterViewInit() {
        this.scrollable.elementScrolled().subscribe(() => {
            const headerStartElement = this.headerStart.nativeElement;
            const footerStartElement = this.footerStart.nativeElement;
            const fixedStartElement = this.fixedStart.nativeElement;
            //const cardElement = this.cardElement._element.nativeElement;
            const headerRect = headerStartElement.getBoundingClientRect();
            const footerRect = footerStartElement.getBoundingClientRect();
            if (headerRect.bottom < 0 && footerRect.top > 950) {
                fixedStartElement.classList.add('fixed-card');
                fixedStartElement.classList.remove('hidden-fixed-card');
                //} else if (footerRect.top > 0) {
                // fixedStartElement.classList.add('fixed-card');
            } else {
                fixedStartElement.classList.remove('fixed-card');
                fixedStartElement.classList.add('hidden-fixed-card');
            }
        });
    }

    private updateSelectedProviders(selectedProvider: Provider | undefined) {
        this.selectedProviders = [];
        this.selectedProvider = selectedProvider;
        let selectedProviderIds: number[] = [];

        if (
            this.selectedProvider?.isWaitlist &&
            this.selectedProvider.selected
        ) {
            this.selectedProviders = [];
            selectedProviderIds = [];
        }

        this._providers.forEach((provider) => {
            //Waitlist can only exist by itself, so we need to deselect others accordingly, or
            // deselect it if others are selected
            if (
                this.selectedProvider?.isWaitlist &&
                this.selectedProvider.selected &&
                this.selectedProvider != provider
            ) {
                provider.selected = false;
            } else {
                if (
                    provider.selected &&
                    provider.providerId &&
                    provider.providerId !== 0
                ) {
                    this.selectedProviders.push(provider);
                    selectedProviderIds.push(provider.providerId);
                    if (
                        this.selectedProvider != provider &&
                        provider.isWaitlist
                    ) {
                        provider.selected = false;
                        this.selectedProviders = this.selectedProviders.filter(
                            (obj) => {
                                return obj !== provider;
                            }
                        );
                        selectedProviderIds = selectedProviderIds.filter(
                            (obj) => {
                                return obj !== provider.providerId;
                            }
                        );
                    }
                }
            }
        });

        // Written unconditionally: the old `length > 0` guard meant clearing your selection left
        // the previous provider staged, so the application was still submitted to it (LA-22939).
        this._pendingSelection.setProviderIds(selectedProviderIds);
    }

    selectProvider(item: Provider) {
        this.updateSelectedProviders(item);
    }

    removeProvider(index: number) {
        if (index >= 0 && index < this.selectedProviders.length)
            this.selectedProviders[index].selected = false;
        this.updateSelectedProviders(this.selectedProviders[index]);
    }

    setSortOrder(value: string, text: string) {
        this.sortOrderText = text;
        this.sortEvent.emit(value);
    }

    private updateSelectedSites() {
        let selectedProviderSiteIds: number[] = [];
        this._providers.forEach((provider) => {
            if (provider.selected && provider.selectedSiteIds) {
                selectedProviderSiteIds = selectedProviderSiteIds.concat(
                    provider.selectedSiteIds
                );
            }
        });

        // Unconditional for the same reason as updateSelectedProviders above (LA-22939).
        this._pendingSelection.setProviderSiteIds(selectedProviderSiteIds);
    }

    startApplication() {
        this.updateSelectedSites();

        if (this._auth.isSignedIn)
            this.router.navigateByUrl(
                `${runtimeConfig.clientCode}/application`
            );
        else this._authPopup.showAccountCreation();
    }
}
